import { useState } from "react";
import {
  useGetAllFeeSharesQuery,
  useAddFeeShareMutation,
  useGetFeeShareByIdQuery,
  useUpdateFeeShareMutation,
  useDeleteFeeShareMutation,
} from "../api/courseFeeShareApi";
import { CourseFeeSharePayloadType } from "../types/courseFeeShareRequest";
import {
  FeeShareApiResponse,
  FeeShareResponseType,
} from "../types/courseFeeShareResponse";
import { showToast } from "../../../../core/utils/alert";

export const useCourseFeeShare = (initialParams = { size: 10, page: 1 }) => {
  const [searchParams, setSearchParams] = useState({
    size: initialParams.size,
    page: initialParams.page,
    search: "",
    session_id: undefined as number | undefined,
    course_id: undefined as number | undefined,
    type_id: undefined as number | undefined,
    share_type: undefined as string | undefined,
  });

  // Get all fee shares with pagination
  const {
    data: feeSharesData,
    isLoading,
    isFetching,
    isError,
    error,
    refetch,
  } = useGetAllFeeSharesQuery(searchParams);

  // Add fee share mutation
  const [addFeeShareMutation, { isLoading: isAdding }] =
    useAddFeeShareMutation();

  // Update fee share mutation
  const [updateFeeShareMutation, { isLoading: isUpdating }] =
    useUpdateFeeShareMutation();

  // Delete fee share mutation
  const [deleteFeeShareMutation, { isLoading: isDeleting }] =
    useDeleteFeeShareMutation();

  // Get fee share by ID hook
  const useGetFeeShareById = (id: string) => {
    const { data, isLoading: isLoadingFeeShare } = useGetFeeShareByIdQuery(
      { id },
      { skip: !id }
    );

    // Create a type assertion to handle both property names
    const responseData = data?.data as
      | {
          feeShare?: FeeShareResponseType;
          coursefeeshare?: FeeShareResponseType;
        }
      | undefined;

    return {
      feeShare: responseData?.feeShare || responseData?.coursefeeshare,
      loading: isLoadingFeeShare,
    };
  };

  // Add fee share handler
  const addFeeShare = async (payload: CourseFeeSharePayloadType) => {
    try {
      const response = await addFeeShareMutation(payload).unwrap();
      return response;
    } catch (error: any) {
      showToast({
        title: "Error",
        text: error.data?.message || "Failed to add fee share",
        icon: "error",
      });
      return { success: false, error };
    }
  };

  // Update fee share handler
  const updateFeeShare = async (
    id: string,
    payload: CourseFeeSharePayloadType
  ) => {
    try {
      const response = await updateFeeShareMutation({ id, payload }).unwrap();
      return response;
    } catch (error: any) {
      showToast({
        title: "Error",
        text: error.data?.message || "Failed to update fee share",
        icon: "error",
      });
      return { success: false, error };
    }
  };

  // Delete fee share handler
  const deleteFeeShare = async (id: string) => {
    try {
      const response = await deleteFeeShareMutation({ id }).unwrap();
      return response;
    } catch (error: any) {
      showToast({
        title: "Error",
        text: error.data?.message || "Failed to delete fee share",
        icon: "error",
      });
      return { success: false, error };
    }
  };

  // Search and pagination handlers
  const handleSearch = (search: string) => {
    setSearchParams((prev) => ({ ...prev, search, page: 1 }));
  };

  const handlePageChange = (page: number) => {
    setSearchParams((prev) => ({ ...prev, page }));
  };

  const handlePageSizeChange = (pageSize: number) => {
    setSearchParams((prev) => ({ ...prev, size: pageSize, page: 1 }));
  };

  const handleFilterChange = (filters: {
    session_id?: number;
    course_id?: number;
    type_id?: number;
    share_type?: string;
  }) => {
    setSearchParams((prev) => ({ ...prev, ...filters, page: 1 }));
  };

  // Cast the API response to the correct type
  const apiResponse = feeSharesData as unknown as FeeShareApiResponse;

  // Extract fee shares and pagination data from API response
  const feeShares = apiResponse?.data?.items || [];
  const totalItems = apiResponse?.data?.total || 0;

  // Transform API data to match the required format for display
  const transformedFeeShares = feeShares.map((item: FeeShareResponseType) => {
    // Calculate average share amount from stageFeeShare entries if available
    let shareAmount = 0;
    if (item.stageFeeShare && item.stageFeeShare.length > 0) {
      // You can decide how to represent share_amount from stageFeeShare:
      // Option 1: Use the first stage's share amount
      shareAmount = item.stageFeeShare[0].share_amount;

      // Option 2: Calculate average (uncomment this if you prefer average)
      // shareAmount = item.stageFeeShare.reduce((sum, stage) => sum + stage.share_amount, 0) /
      //              item.stageFeeShare.length;
    }

    return {
      id: item.id,
      session_id: item.session_id,
      course_id: item.course_id,
      type_id: item.type_id,
      feeType: item.feeType,
      session_name: item.session?.name || "N/A",
      course_name: item.course?.name || "N/A",
      fee_type: "Course Fee",
      share_type: item.share_type,
      share_amount: shareAmount, // Derived from stageFeeShare
      all_centers: item.all_centers,
      centers: item.centers?.map((center) => center.name) || [],
      created_at: item.created_at,
      updated_at: item.updated_at,
    };
  });

  const paginationData = {
    currentPage: searchParams.page,
    pageSize: searchParams.size,
    totalItems,
    totalPages: Math.ceil(totalItems / searchParams.size),
  };

  return {
    // Data
    feeShares: transformedFeeShares,
    paginationData,
    searchTerm: searchParams.search,

    // Loading states
    isLoading: isLoading || isFetching,
    isAdding,
    isUpdating,
    isDeleting,
    isError,
    error,

    // Actions
    refetch,
    handleSearch,
    handlePageChange,
    handlePageSizeChange,
    handleFilterChange,
    addFeeShare,
    updateFeeShare,
    deleteFeeShare,
    useGetFeeShareById,

    // Current filter state
    filters: {
      session_id: searchParams.session_id,
      course_id: searchParams.course_id,
      type_id: searchParams.type_id,
      share_type: searchParams.share_type,
    },
  };
};
